import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useUnityBuildStore } from '../../stores/store';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useSimpleDragManager } from './SimpleDragManager';
import { getAdjustedCursorPosition, calculateRealWorldDistance } from './takeoffHelper';
import { PointAttributeHelpers } from './PointAttributeHelpers';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';

interface SVGPointLineNewProps {
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
}

/**
 * Enhanced SVGPointLine that's aware of DragManager positions
 * Lines follow points smoothly during drag operations
 */
export const SVGPointLineNew: React.FC<SVGPointLineNewProps> = ({ cursorPositionRef }) => {
  const unityBuildStore = useStore(useUnityBuildStore);
  const temporaryPoints = unityBuildStore.temporaryPoints;
  const angleOptions = unityBuildStore.angleOptions;
  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  const userScale = unityBuildStore.userScale;
  const showLineMeasurements = unityBuildStore.showLineMeasurements;
  const { isLassoMode, lassoPoints, cursorPosition, isInsertingPoint, setIsInsertingPoint } =
    unityBuildStore;

  const snapMode = useStore(useKeyBoardEventStore).snapMode;
  const {
    currentTakeoffAssembly,
    currentGlobalAssembly,
    isCreatingAssembly,
    setCurrentTakeoffAssembly,
  } = useStore(useAssemblyStore);
  const { isDragging: isDraggingPoints, getPointPosition } = useSimpleDragManager();
  const { hasCopiedItems } = useStore(useSelectionStore);

  // Insert Point functionality state
  const [hoveredLineIndex, setHoveredLineIndex] = useState<number | null>(null);
  const [hoveredAssemblyId, setHoveredAssemblyId] = useState<string | null>(null);
  const { submitAssembly } = useSubmitTakeoffAssembly();

  // Line dragging state
  const [isDraggingLine, setIsDraggingLine] = useState(false);
  const [draggedLineData, setDraggedLineData] = useState<{
    assemblyId: string;
    lineIndex: number;
    startPos: { x: number; y: number };
    originalPoints: any[];
  } | null>(null);

  // Local cursor position state for efficient trailing line updates
  const [localCursorPosition, setLocalCursorPosition] = useState(cursorPosition);

  // Local tick to force re-render during coordinated drags
  const [, setCoordinatedDragTick] = useState(0);

  // Update local cursor position only when we need trailing line (more efficient)
  useEffect(() => {
    if (currentTakeoffAssembly && !isDraggingPoints) {
      setLocalCursorPosition(cursorPosition);
    }
  }, [cursorPosition, currentTakeoffAssembly, isDraggingPoints]);

  // Listen for coordinated drag events to update line rendering
  useEffect(() => {
    const handleCoordinatedDragUpdate = () => {
      setCoordinatedDragTick(t => t + 1);
    };

    const handleCoordinatedDragEnd = () => {
      setCoordinatedDragTick(t => t + 1);
    };

    document.addEventListener('coordinated:drag-update', handleCoordinatedDragUpdate);
    document.addEventListener('coordinated:drag-end', handleCoordinatedDragEnd);

    return () => {
      document.removeEventListener('coordinated:drag-update', handleCoordinatedDragUpdate);
      document.removeEventListener('coordinated:drag-end', handleCoordinatedDragEnd);
    };
  }, []);

  // Handle line dragging with global mouse events
  useEffect(() => {
    if (!isDraggingLine || !draggedLineData) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Get current cursor position from store (document-relative coordinates)
      const unityStoreState = useUnityBuildStore.getState();
      const currentCursorPos = unityStoreState?.cursorPosition || { x: 0, y: 0 };

      // Calculate delta using transformed coordinates
      const deltaX = currentCursorPos.x - draggedLineData.startPos.x;
      const deltaY = currentCursorPos.y - draggedLineData.startPos.y;

      // Update the current takeoff assembly with new positions
      if (
        currentTakeoffAssembly &&
        currentTakeoffAssembly.assemblyid === draggedLineData.assemblyId
      ) {
        const updatedPoints = draggedLineData.originalPoints.map((point, index) => {
          // Only move the two points connected by this line
          if (index === draggedLineData.lineIndex || index === draggedLineData.lineIndex + 1) {
            return {
              ...point,
              x: point.x + deltaX,
              y: point.y + deltaY,
            };
          }
          return point;
        });

        setCurrentTakeoffAssembly({
          ...currentTakeoffAssembly,
          pointsList: updatedPoints,
        });
      }
    };

    const handleMouseUp = () => {
      if (draggedLineData && currentTakeoffAssembly) {
        // Submit the final positions
        submitAssembly({
          shouldUpdateBreakout: false,
          assembly: currentTakeoffAssembly,
          isUpdating: true,
          pageScale: userScale,
        }).catch(console.error);
      }

      // Clean up
      setIsDraggingLine(false);
      setDraggedLineData(null);
      setCurrentTakeoffAssembly(null); // Clear the current selected assembly
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDraggingLine,
    draggedLineData,
    currentTakeoffAssembly,
    submitAssembly,
    userScale,
    setCurrentTakeoffAssembly,
  ]);

  // Insert Point handlers (defined early to avoid dependency issues)
  const handleLineMouseDown = useCallback(
    (
      e: React.MouseEvent<SVGElement, MouseEvent>,
      pointsList: any,
      lineIndex: number,
      assemblyId: string
    ) => {
      if (e.button === 0) {
        if (isInsertingPoint) {
          // Insert Point mode
          if (assemblyId !== currentTakeoffAssembly?.assemblyid) {
            console.error('Cannot insert point on different assembly than selected');
            return;
          }
          PointAttributeHelpers.handleInsertPoint(
            e,
            pointsList,
            lineIndex,
            currentTakeoffAssembly,
            setCurrentTakeoffAssembly,
            submitAssembly
          );
          setIsInsertingPoint(false); // Reset after insertion
          setCurrentTakeoffAssembly(null);
        } else {
          if (currentTakeoffAssembly) {
            console.log('🚫 Drag prevented - currentTakeoffAssembly exists');
            return;
          }
          // Line dragging mode
          e.preventDefault();
          e.stopPropagation();

          // Find the assembly and set it as current takeoff assembly (current page only)
          const allAssemblies =
            filteredCountedAssemblies?.filteredAssemblies?.flatMap(
              (group: any) => group.assembliesList || []
            ) || [];
          const assembly = allAssemblies.find((a: any) => a.assemblyid === assemblyId);

          if (assembly) {
            // Set this assembly as the current takeoff assembly
            setCurrentTakeoffAssembly({
              ...assembly,
              pointsList: [...(assembly.pointsList || [])],
            });

            // Get current cursor position from store (document-relative coordinates)
            const unityStoreState = useUnityBuildStore.getState();
            const currentCursorPos = unityStoreState?.cursorPosition || { x: 0, y: 0 };

            // Start line dragging
            setIsDraggingLine(true);
            setDraggedLineData({
              assemblyId,
              lineIndex,
              startPos: { x: currentCursorPos.x, y: currentCursorPos.y },
              originalPoints: [...(assembly.pointsList || [])],
            });
          }
        }
      }
    },
    [
      isInsertingPoint,
      currentTakeoffAssembly,
      setCurrentTakeoffAssembly,
      submitAssembly,
      setIsInsertingPoint,
      filteredCountedAssemblies,
    ]
  );

  // Handle line hover effects during insertion mode
  const handleLineMouseEnter = useCallback(
    (e: React.MouseEvent<SVGElement, MouseEvent>, lineIndex: number, assemblyId: string) => {
      if (isInsertingPoint && assemblyId === currentTakeoffAssembly?.assemblyid) {
        setHoveredLineIndex(lineIndex);
        setHoveredAssemblyId(assemblyId);
      }
    },
    [isInsertingPoint, currentTakeoffAssembly]
  );

  const handleLineMouseLeave = useCallback(() => {
    setHoveredLineIndex(null);
    setHoveredAssemblyId(null);
  }, []);

  // Get visual position for a point (from coordinated drag, simple drag, or actual position)
  const getVisualPointPosition = useMemo(() => {
    return (point: any) => {
      // First check for coordinated drag live positions
      const livePositions = (window as any).__takeoffLivePositions;
      if (livePositions && point.pointid && livePositions.has(point.pointid)) {
        const livePos = livePositions.get(point.pointid);
        return { x: livePos.x, y: livePos.y };
      }

      // Then check for simple drag manager positions
      if (isDraggingPoints && point.pointid) {
        const dragPosition = getPointPosition(point.pointid);
        if (dragPosition) {
          return { x: dragPosition.x, y: dragPosition.y };
        }
      }

      return { x: point.x, y: point.y };
    };
  }, [isDraggingPoints, getPointPosition]); // Live positions handled by getVisualPointPosition

  // Build a fast lookup of optimistic points (currentTakeoffAssembly) by id
  const optimisticPointById = useMemo(() => {
    const map = new Map<string, any>();
    const list = currentTakeoffAssembly?.pointsList || [];
    for (const pt of list as any[]) {
      if (pt?.pointid) {
        map.set(pt.pointid, pt);
      }
    }
    return map;
  }, [currentTakeoffAssembly]);

  // Normalize allowTrailingLine flag with tolerant property-name handling and string values
  const allowsLineToPoint = useMemo(() => {
    return (point: any): boolean => {
      const source = (point?.pointid && optimisticPointById.get(point.pointid)) || point;
      const raw =
        (source as any)?.allowtrailingline ??
        (source as any)?.allowTrailingLine ??
        (source as any)?.allowTrailing ??
        (source as any)?.allow_trailing_line;

      if (typeof raw === 'string') {
        const s = raw.toLowerCase();
        return !(s === 'false' || s === '0' || s === 'no');
      }
      if (raw === false) return false;
      return true; // default to allowed when undefined
    };
  }, [optimisticPointById]);

  // Render lines between points for length/area assemblies
  const renderLinesBetweenPoints = useMemo(() => {
    return (pointsList: any[], assembly: any) => {
      if (!pointsList || pointsList.length < 2) return null;

      const lines = [];
      for (let i = 0; i < pointsList.length - 1; i++) {
        const currentPoint = pointsList[i];
        const nextPoint = pointsList[i + 1];

        // Minimal validity check: require numeric coordinates; do not block on evaluatePoint
        const currentValid =
          currentPoint && typeof currentPoint.x === 'number' && typeof currentPoint.y === 'number';
        const nextValid =
          nextPoint && typeof nextPoint.x === 'number' && typeof nextPoint.y === 'number';
        if (!currentValid || !nextValid) {
          continue;
        }

        // No need to skip lines - simple drag system renders live positions directly

        // Check if a line should be drawn TO the next point
        // Destination point governs the incoming segment visibility
        if (!allowsLineToPoint(nextPoint)) {
          continue;
        }

        // Get visual positions (may be different during drag)
        const currentPos = getVisualPointPosition(currentPoint);
        const nextPos = getVisualPointPosition(nextPoint);

        // Determine line styling for Insert Point mode
        const isHoveredLine =
          isInsertingPoint && hoveredLineIndex === i && hoveredAssemblyId === assembly.assemblyid;

        const lineStroke = isHoveredLine ? '#FF6B35' : assembly.linecolor || 'red';
        const lineWidth = isHoveredLine ? (assembly.linesize || 2) + 2 : assembly.linesize || 2;

        lines.push(
          <g key={`line-group-${assembly.assemblyid}-${i}`}>
            <line
              key={`line-${assembly.assemblyid}-${i}`}
              x1={currentPos.x}
              y1={currentPos.y}
              x2={nextPos.x}
              y2={nextPos.y}
              stroke={lineStroke}
              strokeWidth={lineWidth}
              strokeDasharray={assembly.linedasharray || ''}
              strokeOpacity={assembly.fillopacity || 0.5}
              onMouseDown={e => handleLineMouseDown(e, pointsList, i, assembly.assemblyid)}
              onMouseEnter={e => handleLineMouseEnter(e, i, assembly.assemblyid)}
              onMouseLeave={handleLineMouseLeave}
              style={{
                cursor:
                  currentTakeoffAssembly && !isDraggingLine
                    ? 'crosshair'
                    : isInsertingPoint
                      ? 'crosshair'
                      : isDraggingLine
                        ? 'grabbing'
                        : 'grab',
              }}
            />

            {/* Distance measurement and scale warning */}
            {(() => {
              const pixelDistance = Math.sqrt(
                (nextPos.x - currentPos.x) ** 2 + (nextPos.y - currentPos.y) ** 2
              );
              const midX = (currentPos.x + nextPos.x) / 2;
              const midY = (currentPos.y + nextPos.y) / 2;

              if (userScale && showLineMeasurements) {
                // Show real-world measurements when scale is set AND measurements are enabled
                const realWorldDistance = calculateRealWorldDistance(pixelDistance, userScale);
                let feet = Math.floor(realWorldDistance);
                let inches = Math.round((realWorldDistance - feet) * 12);

                // Convert 12+ inches to additional feet
                if (inches >= 12) {
                  feet += Math.floor(inches / 12);
                  inches = inches % 12;
                }

                return (
                  <>
                    <rect
                      x={midX - 25}
                      y={midY - 18}
                      width="50"
                      height="16"
                      fill="white"
                      fillOpacity={0.95}
                      stroke="#ccc"
                      strokeWidth={0.3}
                      rx={1}
                      pointerEvents="none"
                    />
                    <text
                      x={midX}
                      y={midY - 6}
                      fill="#333"
                      fontSize="9"
                      fontWeight="400"
                      textAnchor="middle"
                      fontFamily="Arial, sans-serif"
                      pointerEvents="none"
                    >
                      {`${feet}' ${inches}"`}
                    </text>
                  </>
                );
              } else if (!userScale) {
                // ALWAYS show scale warning when no scale is set (regardless of toggle)
                return (
                  <>
                    <rect
                      x={midX - 65}
                      y={midY - 20}
                      width="130"
                      height="20"
                      fill="#FF6B35"
                      fillOpacity={0.95}
                      stroke="#D32F2F"
                      strokeWidth={2}
                      rx={4}
                      pointerEvents="none"
                    />
                    <text
                      x={midX}
                      y={midY - 6}
                      fill="white"
                      fontSize="12"
                      fontWeight="bold"
                      textAnchor="middle"
                      pointerEvents="none"
                    >
                      ⚠️ NO SCALE SET!
                    </text>
                  </>
                );
              }
              return null; // No scale and measurements disabled - show nothing
            })()}
          </g>
        );
      }

      // Add closing line for area assemblies (from last point back to first point)
      if (assembly.assemblymeasurementtype === 'area' && pointsList.length >= 3) {
        const firstPoint = pointsList[0];
        const lastPoint = pointsList[pointsList.length - 1];

        if (
          firstPoint &&
          lastPoint &&
          typeof firstPoint.x === 'number' &&
          typeof firstPoint.y === 'number' &&
          typeof lastPoint.x === 'number' &&
          typeof lastPoint.y === 'number'
        ) {
          // Render closing line with live positions
          const firstPos = getVisualPointPosition(firstPoint);
          const lastPos = getVisualPointPosition(lastPoint);

          // Use a special line index for the closing line (length of array)
          const closingLineIndex = pointsList.length - 1;
          const isHoveredClosingLine =
            isInsertingPoint &&
            hoveredLineIndex === closingLineIndex &&
            hoveredAssemblyId === assembly.assemblyid;

          const lineStroke = isHoveredClosingLine ? '#FF6B35' : assembly.linecolor || 'red';
          const lineWidth = isHoveredClosingLine
            ? (assembly.linesize || 2) + 2
            : assembly.linesize || 2;

          lines.push(
            <g key={`closing-line-group-${assembly.assemblyid}`}>
              <line
                key={`closing-line-${assembly.assemblyid}`}
                x1={lastPos.x}
                y1={lastPos.y}
                x2={firstPos.x}
                y2={firstPos.y}
                stroke={lineStroke}
                strokeWidth={lineWidth}
                strokeDasharray={assembly.linedasharray || ''}
                strokeOpacity={assembly.fillopacity || 0.5}
                onMouseDown={e =>
                  handleLineMouseDown(e, pointsList, closingLineIndex, assembly.assemblyid)
                }
                onMouseEnter={e => handleLineMouseEnter(e, closingLineIndex, assembly.assemblyid)}
                onMouseLeave={handleLineMouseLeave}
                style={{
                  cursor:
                    currentTakeoffAssembly && !isDraggingLine
                      ? 'crosshair'
                      : isInsertingPoint
                        ? 'crosshair'
                        : isDraggingLine
                          ? 'grabbing'
                          : 'grab',
                }}
              />

              {/* Distance measurement for closing line */}
              {(() => {
                const pixelDistance = Math.sqrt(
                  (firstPos.x - lastPos.x) ** 2 + (firstPos.y - lastPos.y) ** 2
                );
                const midX = (lastPos.x + firstPos.x) / 2;
                const midY = (lastPos.y + firstPos.y) / 2;

                if (userScale && showLineMeasurements) {
                  const realWorldDistance = calculateRealWorldDistance(pixelDistance, userScale);
                  let feet = Math.floor(realWorldDistance);
                  let inches = Math.round((realWorldDistance - feet) * 12);

                  if (inches >= 12) {
                    feet += Math.floor(inches / 12);
                    inches = inches % 12;
                  }

                  return (
                    <>
                      <rect
                        x={midX - 25}
                        y={midY - 18}
                        width="50"
                        height="16"
                        fill="white"
                        fillOpacity={0.95}
                        stroke="#ccc"
                        strokeWidth={0.3}
                        rx={1}
                        pointerEvents="none"
                      />
                      <text
                        x={midX}
                        y={midY - 6}
                        fill="#333"
                        fontSize="9"
                        fontWeight="400"
                        textAnchor="middle"
                        fontFamily="Arial, sans-serif"
                        pointerEvents="none"
                      >
                        {`${feet}' ${inches}"`}
                      </text>
                    </>
                  );
                }
                return null;
              })()}
            </g>
          );
        }
      }

      return lines;
    };
  }, [
    getVisualPointPosition,
    allowsLineToPoint,
    isInsertingPoint,
    hoveredLineIndex,
    hoveredAssemblyId,
    handleLineMouseDown,
    handleLineMouseEnter,
    handleLineMouseLeave,
    userScale,
    showLineMeasurements,
    isDraggingLine,
    currentTakeoffAssembly,
  ]);

  // Render trailing line from last point to cursor
  const renderTrailingLine = useMemo(() => {
    return (pointsList: any[], assembly: any) => {
      if (!pointsList || pointsList.length === 0) return null;
      if (!cursorPositionRef?.current) return null;
      if (isDraggingLine) return null;

      const lastPoint = pointsList[pointsList.length - 1];
      // Only require numeric coords for preview
      if (!lastPoint || typeof lastPoint.x !== 'number' || typeof lastPoint.y !== 'number') {
        return null;
      }

      // Trailing line always shows when actively adding points for better UX
      // allowtrailingline only controls lines TO points, not FROM the last point to cursor

      // Get visual position for the last point
      const lastPointPos = getVisualPointPosition(lastPoint);

      // Use localCursorPosition for reactivity, fallback to cursorPositionRef
      const currentCursor = localCursorPosition || cursorPositionRef.current;

      // Apply polar tracking to adjust the cursor position
      const adjustedCursor = getAdjustedCursorPosition(
        { ...lastPoint, ...lastPointPos }, // Merge point data with visual position
        currentCursor,
        angleOptions,
        snapMode,
        assembly
      );

      // Calculate dynamic distance for measurement
      const pixelDistance = Math.sqrt(
        (adjustedCursor.x - lastPointPos.x) ** 2 + (adjustedCursor.y - lastPointPos.y) ** 2
      );

      return (
        <>
          <line
            x1={lastPointPos.x}
            y1={lastPointPos.y}
            x2={adjustedCursor.x}
            y2={adjustedCursor.y}
            stroke={assembly?.linecolor || 'red'}
            strokeWidth={assembly?.linesize || 2}
            strokeDasharray={assembly?.linedasharray || ''}
            strokeOpacity={assembly?.fillopacity || 0.5}
          />

          {/* Dynamic measurement on trailing line */}
          {showLineMeasurements &&
            userScale &&
            pixelDistance > 10 &&
            (() => {
              const realWorldDistance = calculateRealWorldDistance(pixelDistance, userScale);
              let feet = Math.floor(realWorldDistance);
              let inches = Math.round((realWorldDistance - feet) * 12);

              // Convert 12+ inches to additional feet
              if (inches >= 12) {
                feet += Math.floor(inches / 12);
                inches = inches % 12;
              }
              const midX = (lastPointPos.x + adjustedCursor.x) / 2;
              const midY = (lastPointPos.y + adjustedCursor.y) / 2;

              return (
                <>
                  <rect
                    x={midX - 25}
                    y={midY - 18}
                    width="50"
                    height="16"
                    fill="rgba(255, 255, 255, 0.9)"
                    stroke="#4285F4"
                    strokeWidth={0.5}
                    rx={2}
                    pointerEvents="none"
                  />
                  <text
                    x={midX}
                    y={midY - 6}
                    fill="#4285F4"
                    fontSize="9"
                    fontWeight="500"
                    textAnchor="middle"
                    fontFamily="Arial, sans-serif"
                    pointerEvents="none"
                  >
                    {`${feet}' ${inches}"`}
                  </text>
                </>
              );
            })()}
        </>
      );
    };
  }, [
    getVisualPointPosition,
    cursorPositionRef,
    angleOptions,
    snapMode,
    localCursorPosition,
    showLineMeasurements,
    userScale,
    isDraggingLine,
  ]);

  // Render lasso path
  const lassoPath = useMemo(() => {
    if (!isLassoMode || !lassoPoints || lassoPoints.length < 2) return null;

    const pathData = lassoPoints.reduce((path, point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;
      return `${path} L ${point.x} ${point.y}`;
    }, '');

    return (
      <path
        d={pathData}
        stroke="#4285F4"
        strokeWidth={2}
        fill="rgba(66, 133, 244, 0.1)"
        strokeDasharray="5,5"
      />
    );
  }, [isLassoMode, lassoPoints]);

  // Main render logic - unified state approach
  const assemblyLines = useMemo(() => {
    if (
      !filteredCountedAssemblies ||
      Array.isArray(filteredCountedAssemblies) ||
      !filteredCountedAssemblies.filteredAssemblies
    )
      return null;

    const allAssemblies = filteredCountedAssemblies.filteredAssemblies.flatMap(
      (group: any) => group.assembliesList || []
    );

    // Check if currentTakeoffAssembly exists but doesn't have a matching assembly in filteredCountedAssemblies
    // This happens when selecting from FolderNodes (new assembly creation)
    const hasMatchingAssembly =
      currentTakeoffAssembly &&
      allAssemblies.some(
        (assembly: any) => assembly.assemblyid === currentTakeoffAssembly.assemblyid
      );

    // Always include existing filtered assemblies, and add currentTakeoffAssembly if it's new
    const assembliesToRender = hasMatchingAssembly
      ? allAssemblies
      : currentTakeoffAssembly
        ? [...allAssemblies, currentTakeoffAssembly] // Show both existing and new assembly
        : allAssemblies;

    return assembliesToRender.map((assembly: any) => {
      // Only render lines for length and area assemblies
      if (
        assembly.assemblymeasurementtype !== 'length' &&
        assembly.assemblymeasurementtype !== 'area'
      ) {
        return null;
      }

      const isCurrentAssembly = assembly.assemblyid === currentTakeoffAssembly?.assemblyid;

      // UNIFIED STATE: Always use filteredCountedAssemblies as source of truth
      // Apply currentTakeoffAssembly changes as overlay only when actively editing
      let pointsToRender = assembly.pointsList || [];
      let assemblyToRender = assembly;

      // Override with currentTakeoffAssembly data only during active editing
      if (isCurrentAssembly && currentTakeoffAssembly?.pointsList) {
        pointsToRender = currentTakeoffAssembly.pointsList;
        assemblyToRender = { ...assembly, ...currentTakeoffAssembly };
      }

      if (!pointsToRender || pointsToRender.length === 0) return null;

      return (
        <g key={`assembly-lines-${assembly.assemblyid}`}>
          {/* Lines between points */}
          {renderLinesBetweenPoints(pointsToRender, assemblyToRender)}

          {/* Trailing line (for current assembly when actively adding points) */}
          {(isCurrentAssembly || isCreatingAssembly) &&
            !isDraggingPoints &&
            !isInsertingPoint &&
            !hasCopiedItems &&
            renderTrailingLine(pointsToRender, assemblyToRender)}
        </g>
      );
    });
  }, [
    filteredCountedAssemblies,
    currentTakeoffAssembly,
    isCreatingAssembly,
    isDraggingPoints,
    isInsertingPoint,
    hasCopiedItems,
    renderTrailingLine,
    renderLinesBetweenPoints,
  ]);

  // Remove overlay - unified rendering handles this above

  // Global assembly lines (when creating a new takeoff from a global assembly)
  // During creation, new points are staged in Unity store's temporaryPoints
  const globalAssemblyLines = useMemo(() => {
    // Show for any active global assembly that is length/area while points are being staged
    if (!currentGlobalAssembly) return null;
    if (
      currentGlobalAssembly.assemblymeasurementtype !== 'length' &&
      currentGlobalAssembly.assemblymeasurementtype !== 'area'
    ) {
      return null;
    }

    // Use temporaryPoints as the working points list while creating
    const workingPoints = (temporaryPoints as any[]) || [];
    if (!workingPoints.length) return null;

    return (
      <g key={`global-assembly-lines-${currentGlobalAssembly.assemblyid}`}>
        {renderLinesBetweenPoints(workingPoints, {
          ...currentGlobalAssembly,
          pointsList: workingPoints,
          linecolor: 'blue',
          linesize: 2,
          linedasharray: '',
          fillopacity: 0.5,
        })}
        {!isDraggingPoints &&
          !isInsertingPoint &&
          !hasCopiedItems &&
          renderTrailingLine(workingPoints, {
            ...currentGlobalAssembly,
            pointsList: workingPoints,
            linecolor: 'blue',
            linesize: 2,
            linedasharray: '',
            fillopacity: 0.5,
          })}
      </g>
    );
  }, [
    currentGlobalAssembly,
    temporaryPoints,
    isDraggingPoints,
    isInsertingPoint,
    hasCopiedItems,
    renderLinesBetweenPoints,
    renderTrailingLine,
  ]);

  // Temporary points lines (during annotation)
  const temporaryLines = useMemo(() => {
    if (!temporaryPoints || temporaryPoints.length === 0) return null;

    // Prefer current takeoff assembly styling when present; otherwise synthesize from global
    const styleAssembly =
      (currentTakeoffAssembly as any) ||
      (currentGlobalAssembly as any) ||
      ({ linecolor: 'blue', linesize: 2, linedasharray: '', fillopacity: 0.5 } as any);

    return (
      <g key="temporary-lines">
        {renderLinesBetweenPoints(temporaryPoints as any[], styleAssembly)}
        {!isDraggingPoints &&
          !isInsertingPoint &&
          !hasCopiedItems &&
          renderTrailingLine(temporaryPoints as any[], styleAssembly)}
      </g>
    );
  }, [
    temporaryPoints,
    currentTakeoffAssembly,
    currentGlobalAssembly,
    isDraggingPoints,
    isInsertingPoint,
    hasCopiedItems,
    renderTrailingLine,
    renderLinesBetweenPoints,
  ]);

  return (
    <>
      {assemblyLines}
      {globalAssemblyLines}
      {temporaryLines}
      {lassoPath}
    </>
  );
};
